import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { TABLE_CONTRIBUTION_FORM_KEYS } from "@shared/constants/contributions";
import { parseDateLocal, formatCurrency } from "@/lib/utils";
import { Check, AlertTriangle } from "lucide-react";
import PerRateContributions from "./PerRateContributions";

type Props = {
  planId: string;
  clientId?: string;
  // DEV-148: when set, GET filters this plan's contributions to one rule set
  // and PUT writes scoped to it. Multi-group editing is driven by the
  // EligibilityGroupsPanel selecting the active group's rule set.
  activeRuleSetId?: number | null;
};

type ContributionPayer = "employer" | "employee" | "base";
type Methodology = "table" | "tier" | "age";

// Single source for the tier editor's key ↔ coverage-tier mapping (used by the
// prefill, the save builder, and the price/split display).
const TIER_KEY_TO_COVERAGE: Record<string, string> = {
  employee: "employee_only",
  spouse: "employee_spouse",
  oneChild: "employee_children",
  threeChildren: "family",
};
const COVERAGE_TO_TIER_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(TIER_KEY_TO_COVERAGE).map(([key, coverage]) => [coverage, key]),
);

export default function PlanContributions({ planId, clientId, activeRuleSetId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Plan info (for filtering base plans by type)
  const { data: plan } = useQuery({
    queryKey: ["/api/plans", planId],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}`);
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Get client plans for the current client to find plans in the same plan year
  const { data: clientPlansData = [] } = useQuery({
    queryKey: ["/api/client-plans", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const res = await fetch(`/api/client-plans?clientId=${clientId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  // Get current plan's client plan association to determine plan year
  const currentClientPlan = useMemo(() => {
    return (clientPlansData as any[]).find((cp: any) => String(cp.planId) === String(planId));
  }, [clientPlansData, planId]);

  // Calculate plan year from effective date
  const currentPlanYear = useMemo(() => {
    if (!currentClientPlan?.effectiveDate) return null;
    const date = parseDateLocal(currentClientPlan.effectiveDate);
    return date ? date.getFullYear() : null;
  }, [currentClientPlan]);

  // Filter base plan options to same client + same plan year + same type
  const basePlanOptions = useMemo(() => {
    const type = plan?.type;
    if (!clientId || !currentPlanYear) return [];
    
    // Get all plan IDs from clientPlans that match the same client and plan year
    const eligiblePlanIds = (clientPlansData as any[])
      .filter((cp: any) => {
        if (String(cp.planId) === String(planId)) return false; // Exclude current plan
        const effectiveDate = parseDateLocal(cp.effectiveDate);
        return effectiveDate ? effectiveDate.getFullYear() === currentPlanYear : false;
      })
      .map((cp: any) => cp.planId);

    // Return plans with matching IDs and same type
    return (clientPlansData as any[])
      .filter((cp: any) => eligiblePlanIds.includes(cp.planId))
      .filter((cp: any) => !type || cp.plan?.type === type)
      .map((cp: any) => ({
        id: cp.planId,
        name: cp.plan?.name || `Plan ${cp.planId}`,
        type: cp.plan?.type,
      }));
  }, [clientPlansData, plan, planId, clientId, currentPlanYear]);

  // Load existing contribution settings (all, then pick current plan)
  const { data: allContribs = [] } = useQuery({
    queryKey: ["/api/plan-contributions"],
    queryFn: async () => {
      const res = await fetch(`/api/plan-contributions`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const existingRows = useMemo(() => {
    // DEV-148: when a specific rule set is active, scope rows to it so the
    // editor only sees that group's contributions. Otherwise show all rows
    // for the plan (legacy single-group behavior).
    return (allContribs as any[]).filter((c) => {
      if (c.planId !== parseInt(planId)) return false;
      if (activeRuleSetId != null && c.contributionRuleSetId !== activeRuleSetId) return false;
      return true;
    });
  }, [allContribs, planId, activeRuleSetId]);

  // Determine if plan has saved rates to enable per-rate contributions
  const { data: planRatesView } = useQuery({
    queryKey: clientId
      ? ["/api/plan-rates/", planId, clientId]
      : ["/api/plan-rates/", planId],
    queryFn: async () => {
      const url = clientId
        ? `/api/plan-rates/${planId}?clientId=${clientId}`
        : `/api/plan-rates/${planId}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      return res.json();
    },
  });

  const hasSavedRates = Boolean(
    (planRatesView as any)?.rateStructure &&
      Array.isArray((planRatesView as any)?.rates) &&
      (planRatesView as any)?.rates.length > 0
  );

  // Per-tier monthly premium — used to show the plan price and the implied
  // employee/employer split next to each contribution row.
  const rateByTier = useMemo(() => {
    const map = new Map<string, number>();
    for (const rate of (((planRatesView as any)?.rates ?? []) as any[])) {
      if (rate?.coverageTier != null) {
        map.set(String(rate.coverageTier), Number(rate.monthlyRate));
      }
    }
    return map;
  }, [planRatesView]);

  // Local UI state
  const [payer, setPayer] = useState<ContributionPayer>("employer");
  const [methodology, setMethodology] = useState<Methodology>("tier");
  const [basePlanId, setBasePlanId] = useState<string>("");

  // DEV-148: affordability floor — when the declared employer amount falls
  // below `percentage` of `basePlanId`'s premium, the calc layer renders the
  // floor instead. Single declared policy, ACA-safe by construction.
  const [affordabilityFloorEnabled, setAffordabilityFloorEnabled] = useState(false);
  const [affordabilityFloorBasePlanId, setAffordabilityFloorBasePlanId] = useState<string>("");
  const [affordabilityFloorPercentage, setAffordabilityFloorPercentage] = useState<string>("70");

  // Table methodology inputs
  type AmountType = "percentage" | "dollar";
  const [values, setValues] = useState<Record<string, { type: AmountType; amount: string; max?: string }>>({
    employee: { type: "percentage", amount: "" },
    spouse: { type: "percentage", amount: "" },
    oneChild: { type: "percentage", amount: "" },
    twoChildren: { type: "percentage", amount: "" },
    threeChildren: { type: "percentage", amount: "" },
  });

  // Prefill from existing contribution rows (new normalized schema)
  useEffect(() => {
    if (!Array.isArray(existingRows) || existingRows.length === 0) return;

    // EASE-imported shape: payer="base" with flat employee/employer dollars, no
    // linked base plan and no %/nominal. The editor has no native "base flat"
    // mode, so load it as Employer-pays-dollar per tier (employee = premium −
    // employer reproduces the exact saved split). This shows the imported
    // numbers in the normal editor instead of an empty Base-Plan form; saving
    // migrates the row to the employer/nominal shape with no change to cost.
    const isFlatBaseImport = (existingRows as any[]).every(
      (r: any) =>
        r.payer === "base" &&
        !r.basePlanId &&
        (r.employeeAmount != null || r.employerAmount != null) &&
        (r.percentage == null || r.percentage === "") &&
        (r.nominalAmount == null || r.nominalAmount === ""),
    );
    if (isFlatBaseImport) {
      const nextValues: any = {};
      for (const r of existingRows as any[]) {
        const key = COVERAGE_TO_TIER_KEY[r.contributionTypeValue];
        if (!key) continue;
        nextValues[key] = {
          type: "dollar",
          amount: String(r.employerAmount ?? "0"),
          max: "",
        };
      }
      setPayer("employer");
      setMethodology("tier");
      setBasePlanId("");
      setValues((prev) => ({ ...prev, ...nextValues }));
      return;
    }

    const anyRow = existingRows[0] as any;
    const inferredPayer = (anyRow.payer as ContributionPayer) || "employer";
    setPayer(inferredPayer);
    const hasTier = existingRows.some((r: any) => r.contributionType === "tier_rates");
    const hasTable = existingRows.some((r: any) => r.contributionType === "table_rates");
    const hasAge = existingRows.some((r: any) => r.contributionType === "age_rates");
    setMethodology((hasAge ? "age" : hasTier ? "tier" : hasTable ? "table" : "tier") as Methodology);
    const baseId = existingRows.find((r: any) => r.basePlanId)?.basePlanId;
    setBasePlanId(baseId ? String(baseId) : "");

    const nextValues: any = {};
    const setFrom = (
      key: string,
      predicate: (r: any) => boolean
    ) => {
      const row = existingRows.find(predicate) as any;
      if (!row) return;
      const isPct = row.percentage != null && row.percentage !== "";
      nextValues[key] = {
        type: isPct ? "percentage" : "dollar",
        amount: isPct ? String(row.percentage) : String(row.nominalAmount || ""),
        max: row.maxAmount != null ? String(row.maxAmount) : "",
      };
    };

    // Tier mapping
    setFrom("employee", (r) => r.contributionType === "tier_rates" && r.contributionTypeValue === "employee_only");
    setFrom("spouse", (r) => r.contributionType === "tier_rates" && r.contributionTypeValue === "employee_spouse");
    setFrom("oneChild", (r) => r.contributionType === "tier_rates" && r.contributionTypeValue === "employee_children");
    setFrom("threeChildren", (r) => r.contributionType === "tier_rates" && r.contributionTypeValue === "family");

    // Table mapping - NEW format (without "Employee +" prefix for dependents)
    // Also supports backward compatibility with old format
    setFrom("employee", (r) => 
      r.contributionType === "table_rates" && 
      r.contributionTypeValue === "Employee"
    );
    setFrom("spouse", (r) => 
      r.contributionType === "table_rates" && 
      (r.contributionTypeValue === "Spouse" || r.contributionTypeValue === "Employee + Spouse")
    );
    setFrom("oneChild", (r) => 
      r.contributionType === "table_rates" && 
      (r.contributionTypeValue === "1 Child" || r.contributionTypeValue === "Employee + 1 Children")
    );
    setFrom("twoChildren", (r) => 
      r.contributionType === "table_rates" && 
      (r.contributionTypeValue === "2 Children" || r.contributionTypeValue === "Employee + 2 Children")
    );
    setFrom("threeChildren", (r) => 
      r.contributionType === "table_rates" && 
      (r.contributionTypeValue === "3+ Children" || r.contributionTypeValue === "Employee + 3+ Children")
    );
    setFrom("oneChildSpouse", (r) => 
      r.contributionType === "table_rates" && 
      (r.contributionTypeValue === "1 Child + Spouse" || r.contributionTypeValue === "Employee + 1 Children + Spouse")
    );
    setFrom("twoChildrenSpouse", (r) => 
      r.contributionType === "table_rates" && 
      (r.contributionTypeValue === "2 Children + Spouse" || r.contributionTypeValue === "Employee + 2 Children + Spouse")
    );
    setFrom("threeChildrenSpouse", (r) => 
      r.contributionType === "table_rates" && 
      (r.contributionTypeValue === "3+ Children + Spouse" || r.contributionTypeValue === "Employee + 3+ Children + Spouse")
    );

    setValues((prev) => ({ ...prev, ...nextValues }));

    // DEV-148: prefill affordability floor from any row that has one.
    const rowWithFloor = existingRows.find((r: any) => r.affordabilityFloor);
    const floor = (rowWithFloor as any)?.affordabilityFloor;
    if (floor && floor.type === "percent_of_plan") {
      setAffordabilityFloorEnabled(true);
      setAffordabilityFloorBasePlanId(
        floor.basePlanId != null ? String(floor.basePlanId) : "",
      );
      setAffordabilityFloorPercentage(
        floor.percentage != null ? String(floor.percentage) : "70",
      );
    } else {
      setAffordabilityFloorEnabled(false);
    }
  }, [existingRows]);

  // When switching to Base Plan payer, clear fields per spec
  useEffect(() => {
    if (payer === "base") {
      setValues({
        employee: { type: "percentage", amount: "" },
        spouse: { type: "percentage", amount: "" },
        oneChild: { type: "percentage", amount: "" },
        twoChildren: { type: "percentage", amount: "" },
        threeChildren: { type: "percentage", amount: "" },
      });
    }
  }, [payer]);

  // If a base plan is chosen, apply its contribution structure into the UI
  useEffect(() => {
    if (payer !== "base" || !basePlanId) return;
    const base = (allContribs as any[]).find((c) => c.planId === parseInt(basePlanId));
    if (!base) return;
    setMethodology((base.contributionMethod as Methodology) || "tier");
    setValues({
      employee: {
        type: (base.employeeContributionType as AmountType) || "percentage",
        amount: base.employeeContribution || "",
        max: base.employeeMaxAmount || "",
      },
      spouse: {
        type: (base.spouseContributionType as AmountType) || "percentage",
        amount: base.spouseContribution || "",
        max: base.spouseMaxAmount || "",
      },
      oneChild: {
        type: (base.oneChildContributionType as AmountType) || "percentage",
        amount: base.oneChildContribution || "",
        max: base.oneChildMaxAmount || "",
      },
      twoChildren: {
        type: (base.twoChildrenContributionType as AmountType) || "percentage",
        amount: base.twoChildrenContribution || "",
        max: base.twoChildrenMaxAmount || "",
      },
      threeChildren: {
        type: (base.threeChildrenContributionType as AmountType) || "percentage",
        amount: base.threeChildrenContribution || "",
        max: base.threeChildrenMaxAmount || "",
      },
    });
  }, [payer, basePlanId, allContribs]);

  const validate = (): string | null => {
    if (payer === "base") {
      if (!basePlanId) return "Please select a base plan.";
      return null;
    }
    // For By Age methodology, skip tier-level validation (handled per-rate below)
    if (methodology === "age") return null;
    // For employer/employee, require amounts for relevant coverage rows
    const requiredKeys = methodology === "table"
      ? [
          "employee",
          "spouse",
          "oneChild",
          "twoChildren",
          "threeChildren",
          "oneChildSpouse",
          "twoChildrenSpouse",
          "threeChildrenSpouse",
        ]
      : [
          // For tier methodology, align to 4 standard tiers
          "employee",
          "spouse",
          "oneChild",
          "threeChildren",
        ];
    for (const k of requiredKeys) {
      const v = (values as any)[k];
      if (!v || v.amount === "") return "Please enter all contribution amounts.";
      const n = Number.parseFloat(v.amount);
      if (!Number.isFinite(n) || n < 0) return "Amounts must be positive numbers.";
      if (v.type === "percentage" && (n < 0 || n > 100)) return "Percentages must be between 0 and 100.";
    }
    return null;
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      // DEV-148: scope the write to the active eligibility group's rule set so
      // other groups' contributions on this plan aren't wiped.
      const url =
        activeRuleSetId != null
          ? `/api/plan-contributions/${planId}?ruleSetId=${activeRuleSetId}`
          : `/api/plan-contributions/${planId}`;
      return apiRequest("PUT", url, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/plan-contributions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/aca/employee-line15-costs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/aca/affordability-preview"] });
      toast({ title: "Saved", description: "Contribution settings saved." , variant: "success"});
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Failed to save", variant: "destructive" });
    },
  });

  // Per-rate contributions saver (only used when rates exist)
  const perRateSaveRef = useRef<() => Promise<void> | undefined>();
  const perRateRowsProviderRef = useRef<
    | (() => Array<{
        planRateId?: number | null;
        contributionType: "tier_rates" | "age_rates";
        contributionTypeValue: string;
        payer?: "employer" | "employee";
        percentage?: number | string | null;
        nominalAmount?: number | string | null;
        maxAmount?: number | string | null;
      }>)
    | undefined
  >(undefined);

  const onSave = async () => {
    const err = validate();
    if (err) {
      toast({ title: "Validation error", description: err, variant: "destructive" });
      return;
    }
    // Build row-based contributions per new API
    const rows: Array<{
      contributionType: "tier_rates" | "table_rates" | "age_rates";
      contributionTypeValue: string;
      payer: "employer" | "employee" | "base";
      percentage?: number | string | null;
      nominalAmount?: number | string | null;
      maxAmount?: number | string | null;
      planRateId?: number | null;
    }> = [];

    if (payer === "base") {
      if (!basePlanId) {
        toast({ title: "Validation error", description: "Please select a base plan.", variant: "destructive" });
        return;
      }
      const ct = methodology === "tier" ? "tier_rates" : methodology === "table" ? "table_rates" : "age_rates";
      rows.push({
        contributionType: ct as any,
        contributionTypeValue: "",
        payer: "base",
        percentage: null,
        nominalAmount: null,
        maxAmount: null,
        // @ts-expect-error send basePlanId for backend normalization
        basePlanId: parseInt(basePlanId),
      });
    } else if (methodology !== "age") {
      if (methodology === "tier") {
        ["employee", "spouse", "oneChild", "threeChildren"].forEach(
          (key) => {
            const v = (values as any)[key] || { type: "percentage", amount: "0" };
            rows.push({
              contributionType: "tier_rates",
              contributionTypeValue: TIER_KEY_TO_COVERAGE[key],
              payer,
              percentage: v.type === "percentage" ? v.amount || "0" : null,
              nominalAmount: v.type === "dollar" ? v.amount || "0" : null,
              maxAmount: v.max || null,
            });
          }
        );
      } else if (methodology === "table") {
        // NEW format: labels without "Employee +" prefix for dependents
        // This allows separate contribution rates for employee vs dependents
        TABLE_CONTRIBUTION_FORM_KEYS.forEach(({ key, label }) => {
          const v = (values as any)[key];
          if (!v) return;
          rows.push({
            contributionType: "table_rates",
            contributionTypeValue: label,
            payer,
            percentage: v.type === "percentage" ? v.amount || "0" : null,
            nominalAmount: v.type === "dollar" ? v.amount || "0" : null,
            maxAmount: v.max || null,
          });
        });
      }
    }

    // Include per-rate rows (age/tier) provided by child so we can delete then insert in one request
    if (hasSavedRates && payer !== "base") {
      const extra = perRateRowsProviderRef.current?.() || [];
      rows.push(
        ...extra.map((r) => ({
          ...r,
          payer: r.payer || (payer as any),
        }))
      );
    }

    // DEV-148: attach affordability floor to every employer-payer row.
    // Calc layer applies max(declared, percentage * basePlanPremium) at fill.
    if (
      affordabilityFloorEnabled &&
      payer === "employer" &&
      affordabilityFloorBasePlanId &&
      affordabilityFloorPercentage
    ) {
      const floor = {
        type: "percent_of_plan" as const,
        basePlanId: Number(affordabilityFloorBasePlanId),
        percentage: Number(affordabilityFloorPercentage),
      };
      for (const r of rows) {
        if ((r as any).payer === "employer") {
          (r as any).affordabilityFloor = floor;
        }
      }
    }

    await saveMutation.mutateAsync({ rows });
  };

  // Resolve the plan price and the resulting employee/employer split for a tier
  // editor row, so the broker sees both parts and that they sum to the price.
  // Only the standard tiers map to a premium; others return null (shown as "—").
  const splitForRow = (key: string) => {
    const coverageTier = TIER_KEY_TO_COVERAGE[key];
    const premium = coverageTier ? rateByTier.get(coverageTier) : undefined;
    if (premium == null || !(premium > 0)) return null;
    const value = values[key];
    const amount = value && value.amount !== "" ? Number(value.amount) : NaN;
    if (!Number.isFinite(amount)) {
      return { premium, employee: null as number | null, employer: null as number | null, valid: null as boolean | null };
    }
    const payerShare = value.type === "percentage" ? (premium * amount) / 100 : amount;
    const employee = payer === "employee" ? payerShare : premium - payerShare;
    const employer = premium - employee;
    const valid = payerShare >= -0.001 && payerShare <= premium + 0.001;
    return { premium, employee, employer, valid };
  };

  const tableRows =
    methodology === "table"
      ? [
          // NEW format: labels without "Employee +" prefix for dependents
          // This makes it clear that contributions apply to dependent portions only
          { key: "employee", label: "Employee" },
          { key: "spouse", label: "Spouse" },
          { key: "oneChild", label: "1 Child" },
          { key: "twoChildren", label: "2 Children" },
          { key: "threeChildren", label: "3+ Children" },
          { key: "oneChildSpouse", label: "1 Child + Spouse" },
          { key: "twoChildrenSpouse", label: "2 Children + Spouse" },
          { key: "threeChildrenSpouse", label: "3+ Children + Spouse" },
        ]
      : [
          // Align tier methodology options with plan rate tiers
          { key: "employee", label: "Employee Only" },
          { key: "spouse", label: "Employee + Spouse" },
          { key: "oneChild", label: "Employee + Child(ren)" },
          { key: "threeChildren", label: "Family" },
        ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contributions</CardTitle>
        <CardDescription>Define contribution source and methodology, then optionally apply per-rate contributions.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Contribution Source</label>
            <Select value={payer} onValueChange={(v: any) => setPayer(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employer">Employer</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="base">Base Plan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Methodology</label>
            <Select value={methodology} onValueChange={(v: any) => setMethodology(v)} disabled={payer === "base"}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="table">Table</SelectItem>
                <SelectItem value="tier">Tier</SelectItem>
                <SelectItem value="age">By Age</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {payer === "base" && (
            <div>
              <label className="block text-sm font-medium mb-1">Base Plan</label>
              <Select value={basePlanId} onValueChange={(v: any) => setBasePlanId(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select base plan" />
                </SelectTrigger>
                <SelectContent>
                  {basePlanOptions.map((p: any) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {payer !== "base" && methodology !== "age" && (
          <div className="space-y-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Coverage</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Max $ (optional)</TableHead>
                  <TableHead className="text-right">Plan price</TableHead>
                  <TableHead>Resulting split</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableRows.map((r) => {
                  const split = splitForRow(r.key);
                  return (
                  <TableRow key={r.key}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell>
                      <Select
                        value={values[r.key]?.type || "percentage"}
                        onValueChange={(v: any) =>
                          setValues((prev) => ({
                            ...prev,
                            [r.key]: { type: v, amount: prev[r.key]?.amount || "", max: prev[r.key]?.max || "" },
                          }))
                        }
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">%</SelectItem>
                          <SelectItem value="dollar">$</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={values[r.key]?.amount || ""}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            [r.key]: { type: prev[r.key]?.type || "percentage", amount: e.target.value, max: prev[r.key]?.max || "" },
                          }))
                        }
                        placeholder={values[r.key]?.type === "dollar" ? "e.g. 200" : "e.g. 80"}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={values[r.key]?.max || ""}
                        onChange={(e) =>
                          setValues((prev) => ({
                            ...prev,
                            [r.key]: { type: prev[r.key]?.type || "percentage", amount: prev[r.key]?.amount || "", max: e.target.value },
                          }))
                        }
                        placeholder="Optional cap"
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {split ? formatCurrency(split.premium) : "—"}
                    </TableCell>
                    <TableCell>
                      {!split || split.employee == null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="flex items-center gap-1.5 font-mono text-xs whitespace-nowrap">
                          EE {formatCurrency(split.employee)} · ER {formatCurrency(split.employer)}
                          {split.valid ? (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          ) : (
                            <span title="The entered share is more than the plan price — the other party would be negative.">
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                            </span>
                          )}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {payer === "employer" && (
          <div className="rounded border p-3 space-y-2 bg-gray-50 dark:bg-gray-900/40">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={affordabilityFloorEnabled}
                onChange={(e) => setAffordabilityFloorEnabled(e.target.checked)}
              />
              Apply affordability floor (ACA-safe minimum)
            </label>
            {affordabilityFloorEnabled && (
              <>
                <p className="text-xs text-muted-foreground">
                  At fill time the rendered employer amount becomes <code>max(declared, percentage × base plan premium)</code>.
                </p>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Base plan</Label>
                    <Select
                      value={affordabilityFloorBasePlanId}
                      onValueChange={setAffordabilityFloorBasePlanId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a plan…" />
                      </SelectTrigger>
                      <SelectContent>
                        {basePlanOptions.map((opt: any) => (
                          <SelectItem key={opt.id} value={String(opt.id)}>
                            {opt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Percentage</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={affordabilityFloorPercentage}
                      onChange={(e) => setAffordabilityFloorPercentage(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">
            {/* Per-rate contributions only exist for the age methodology
                (see the render condition below) — other methodologies get no
                note rather than a promise of a section that never appears. */}
            {methodology === "age" &&
              (hasSavedRates
                ? "Set per-age-band contributions below."
                : "Enter and save plan rates first to enable age-band contributions.")}
          </div>
          <Button onClick={onSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? "Saving..." : "Save Contributions"}
          </Button>
        </div>

        {hasSavedRates && methodology === "age" && (
          <PerRateContributions
            planId={planId}
            clientId={clientId}
            deferSave={true}
            registerSaveHandler={(fn) => {
              perRateSaveRef.current = fn;
            }}
            unstyled
            registerRowsProvider={(fn) => {
              perRateRowsProviderRef.current = fn;
            }}
            payerOverride={payer === "base" ? undefined : payer}
          />
        )}
      </CardContent>
    </Card>
  );
}


